// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'];
export const UPLOAD_CONFIG = {
  IMAGE: { accept: 'image/jpeg,image/jpg,image/png' },
  VIDEO: { accept: 'video/mp4' },
  DOCUMENT: { accept: 'application/pdf' },
};

// Variable parsing regex factories (returns fresh regex objects)
const getPositionalVariableRegex = () => /\{\{(\d+)\}\}/g;
const getNamedVariableRegex = () => /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g;
const getGenericVariableRegex = () => /\{\{([^}]+)\}\}/g;

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const extractNamedVariables = text => {
  const matches = [...text.matchAll(getNamedVariableRegex())];
  return matches.map(match => match[1]);
};

export const hasPositionalVariables = text => {
  return getPositionalVariableRegex().test(text);
};

export const hasNamedVariables = text => {
  return getNamedVariableRegex().test(text);
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(getGenericVariableRegex(), (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(getGenericVariableRegex());
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // Handle different media types
    const mediaType = headerComponent.format.toLowerCase();
    if (mediaType === 'location') {
      // For location templates, include location-specific fields
      allVariables.header.latitude = '';
      allVariables.header.longitude = '';
      allVariables.header.name = '';
      allVariables.header.address = '';
    } else if (mediaType === 'document') {
      // For document templates, include media_name field for filename support
      allVariables.header.media_url = '';
      allVariables.header.media_name = '';
    } else {
      // For image, video templates
      allVariables.header.media_url = '';
    }
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(getGenericVariableRegex()) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};

export const parsePositionalVariables = (
  text,
  maxVariables = Infinity,
  existingExamples = []
) => {
  const examples = [...existingExamples];
  if (hasNamedVariables(text)) {
    return {
      processedText: text,
      examples: [],
      error: {
        key: 'NAMED_IN_POSITIONAL',
      },
    };
  }

  // it keeps track of number of variables
  // in text.replace below it may exceed maxVariables so we are resetting it to maxVariables
  let count = 0;

  const processedText = text.replace(getPositionalVariableRegex(), () => {
    count += 1;
    if (count > maxVariables) {
      return '';
    }
    return `{{${count}}}`;
  });
  count = Math.min(count, maxVariables);

  // if examples length is more than variables found in text remove extra
  if (examples.length > count) {
    examples.splice(count);
  }

  // add extra variable values if needed
  for (let i = examples.length; i < count; i += 1) {
    examples.push('');
  }

  return { processedText, examples, error: {} };
};

export const parseNamedVariables = (text, maxVariables, existingExamples) => {
  if (hasPositionalVariables(text)) {
    return {
      processedText: text,
      examples: [],
      error: {
        key: 'POSITIONAL_IN_NAMED',
      },
    };
  }

  const error = {};

  const variableNames = extractNamedVariables(text);
  const uniqueNames = new Set(variableNames);

  if (variableNames.length !== uniqueNames.size) {
    error.key = 'DUPLICATE_VARIABLES';
  } else if (variableNames.length > maxVariables) {
    error.key = 'MAX_VARIABLES_EXCEEDED';
    error.data = { count: maxVariables };
  }

  // no need to create examples if error
  if (error.key) {
    return {
      processedText: text,
      examples: existingExamples,
      error,
    };
  }

  // Create examples array based on unique variables
  const examples = variableNames.map(param_name => ({
    param_name,
    example:
      existingExamples.find(ex => ex && ex.param_name === param_name)
        ?.example || '',
  }));

  return {
    processedText: text,
    examples,
    error,
  };
};

/**
 * @param {string} text
 * @param {string} parameterType
 * @param {number} maxVariables
 * @param {Array} existingExamples
 * @returns {{
 *   processedText: string,
 *   examples: Array,
 *   error: {key?: string, data?: Record<string, any>}
 * }}
 */
export const parseTemplateVariables = (
  text,
  parameterType,
  maxVariables,
  existingExamples
) => {
  if (parameterType === 'positional') {
    return parsePositionalVariables(text, maxVariables, existingExamples);
  }
  return parseNamedVariables(text, maxVariables, existingExamples);
};

export const buildExamplePropertyByParameterType = (
  exampleData,
  componentType,
  parameterType
) => {
  const positionalKey = `${componentType}_text`;
  const namedKey = `${componentType}_text_named_params`;

  const hasVariables =
    parameterType === 'positional'
      ? exampleData[positionalKey]?.length > 0
      : exampleData[namedKey]?.length > 0;

  if (!hasVariables) {
    return null;
  }

  // WhatsApp API expects body/header examples in specific formats:
  // For positional: { body_text: [["value1", "value2"]] } - NESTED array!
  // For named: { body_text: [{param_name: "name", example: "value"}] }
  const result =
    parameterType === 'positional'
      ? { [positionalKey]: [exampleData[positionalKey]] } // Wrap in array!
      : { [positionalKey]: exampleData[namedKey] };

  return result;
};

export const generateTemplateComponents = (templateData, parameterType) => {
  const components = [];
  const { header, body, footer, buttons } = templateData;

  if (header.enabled) {
    let data = {};
    if (header.format === 'TEXT') {
      data = {
        format: header.format,
        text: header.text,
      };
      const example = buildExamplePropertyByParameterType(
        header.example,
        'header',
        parameterType
      );
      if (example) {
        data.example = example;
      }
    } else if (header.format === 'LOCATION') {
      // Location headers only need format, no media object
      data = {
        format: header.format,
      };
    } else if (MEDIA_FORMATS.includes(header.format)) {
      // Image, Video, Document headers need media with blob_id
      data = {
        format: header.format,
        media: {
          blob_id: header.media.blobId,
        },
      };
    }
    components.push({
      type: header.type,
      ...data,
    });
  }

  // Body component
  const bodyComponent = {
    type: body.type,
    text: body.text,
  };

  const example = buildExamplePropertyByParameterType(
    body.example,
    'body',
    parameterType
  );
  if (example) {
    bodyComponent.example = example;
  }

  components.push(bodyComponent);

  if (footer.enabled) {
    components.push({
      type: footer.type,
      text: footer.text,
    });
  }

  if (buttons.length) {
    const cleanedButtons = buttons.map(b => {
      const cleaned = { ...b };
      delete cleaned.error;

      // Remove empty example arrays for URL buttons with static URLs
      if (
        cleaned.type === 'URL' &&
        Array.isArray(cleaned.example) &&
        cleaned.example.length === 0
      ) {
        delete cleaned.example;
      }

      return cleaned;
    });
    components.push({
      type: COMPONENT_TYPES.BUTTONS,
      buttons: cleanedButtons,
    });
  }

  return components;
};

export const validateTemplateData = (
  templateData,
  parameterType = 'positional'
) => {
  const { header, body, footer, buttons } = templateData;
  const errors = [];
  const warnings = [];

  // Body is required and must have text without errors
  if (body.error) {
    errors.push(`Body: ${body.error}`);
  } else if (!body.text) {
    errors.push('Body: Message text is required');
  }

  // Validate parameter type consistency
  if (body.text) {
    const hasPositional = hasPositionalVariables(body.text);
    const hasNamed = hasNamedVariables(body.text);

    if (parameterType === 'positional' && hasNamed) {
      errors.push(
        'Body: Cannot use named variables like {{customer_name}} when Parameter Type is set to Positional. Switch to Named or use {{1}} {{2}} format.'
      );
    } else if (parameterType === 'named' && hasPositional) {
      errors.push(
        'Body: Cannot use positional variables like {{1}} when Parameter Type is set to Named. Switch to Positional or use {{customer_name}} format.'
      );
    }
  }

  // Header validation (if enabled)
  if (header.enabled) {
    if (header.error) {
      errors.push(`Header: ${header.error}`);
    } else if (header.format === 'TEXT' && !header.text?.length) {
      errors.push('Header: Text is required when header is enabled');
    } else if (header.format === 'LOCATION') {
      // Location headers don't need file upload validation
      // They are validated separately in the HeaderSection component
    } else if (MEDIA_FORMATS.includes(header.format) && !header.media?.blobId) {
      errors.push(`Header: Please upload ${header.format.toLowerCase()} file`);
    }
  }

  // Footer validation (if enabled)
  if (footer.enabled && !footer.text) {
    errors.push('Footer: Text is required when footer is enabled');
  }

  // Button validation
  if (buttons && buttons.length > 0) {
    buttons.forEach((button, index) => {
      if (button.error) {
        errors.push(`Button ${index + 1}: ${button.error}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};
